import { directionTone } from '@/components/ui/market-tone'
import { type Money as MoneyValue, sign } from '@/lib/money'
import { formatSignedRupees } from '@/lib/broker-format'
import { formatPercent, formatSigned, formatSignedNPR } from '@/lib/format'
import { cn } from '@/lib/utils'

interface ChangeProps {
  value: MoneyValue | null | undefined
  /** Render as a percentage rather than an absolute point change. */
  percent?: boolean
  /** Marks a stock that has hit the NEPSE +/-10% circuit. */
  circuit?: boolean
  /** Prefix with "Rs" after the sign: "−Rs 3,164". Not with `percent`. */
  currency?: boolean
  /** Drop the paisa, for a book figure the design shows whole. */
  whole?: boolean
  /**
   * With `currency`, scale to the unit a NEPSE reader uses — "+Rs 1.20 Cr" — for a narrow row where
   * every digit of a crore figure would crowd out its label.
   */
  compact?: boolean
  /**
   * Draw the ▲/▼ glyph. Off by default — the design does not want the
   * triangle repeated at every call site; turn it on only where a frame
   * specifically calls for the mark. The explicit sign and the sr-only
   * direction word still carry the meaning without colour, so the value
   * stays legible in greyscale.
   */
  glyph?: boolean
  className?: string
}

/**
 * The ONLY component besides the chart wrappers that applies market colour.
 *
 * Colour never carries the meaning alone. Around 8% of men have red-green
 * colour blindness, and under deuteranopia the gain and loss tokens are the
 * same colour — so direction is also carried by the glyph and the explicit
 * sign, and the value stays readable in greyscale. A `.cb` class on <html>
 * additionally swaps the token pair to blue/orange.
 */
export default function Change({
  value,
  percent = false,
  circuit = false,
  currency = false,
  whole = false,
  compact = false,
  glyph = false,
  className,
}: ChangeProps) {
  if (value === null || value === undefined) {
    return <span className={cn('tnum text-muted-foreground', className)}>—</span>
  }

  const direction = sign(value)
  const mark = direction > 0 ? '▲' : direction < 0 ? '▼' : '—'
  const label = direction > 0 ? 'up' : direction < 0 ? 'down' : 'unchanged'

  const tone = circuit ? 'text-market-circuit' : directionTone(direction)

  return (
    <span className={cn('tnum inline-flex items-center gap-1', tone, className)} data-numeric>
      {glyph ? <span aria-hidden="true">{mark}</span> : null}
      <span className="sr-only">{label} </span>
      {percent
        ? formatPercent(value)
        : currency
          ? compact
            ? formatSignedRupees(value)
            : formatSignedNPR(value, { whole })
          : formatSigned(value, { whole })}
      {circuit ? <span className="sr-only"> (circuit limit)</span> : null}
    </span>
  )
}
